"use client";
import { useEffect } from "react";
import SearchButton from "@/components/SearchButton";
import { useSearchContext } from "@/lib/hooks/searchContext/searchStore";
import { useTranslation } from "@/hooks/translationContext";
import { useHeader } from "@/hooks/headerContext";
import { useNavigation } from "@/hooks/useNavigation";
import { Button } from "@/components/ui/button";
import ChevronDownIcon from "@heroicons/react/24/outline/ChevronDownIcon";
import { cn } from "@/lib/utils";
import { useCurrentDeviceContext } from "@/context/DeviceContext";

const emptySelection = { id: 0, title: "", slug: "" };

export default function HeroSearchClientComponent() {
  const { setIsOpen, setCurrentTab, setInitialFocus } = useSearchContext();
  const { t } = useTranslation();
  const { district, service, onChange } = useHeader();
  const { device } = useCurrentDeviceContext();
  const isMobile = device === "mobile";

  const hasDistrict = !!district?.id;
  const hasService = !!service?.id;

  const inputPlaceholder = t("header-quicksearch-placeholder", {
    defaultValue: "Mekan Arayın",
  });

  const districtLabel = t("header-quicksearch-district", {
    defaultValue: "Semt Seçin",
  });

  const serviceLabel = t("header-quicksearch-service", {
    defaultValue: "Hizmet Seçin",
  });

  const searchLabel = t("text-search", { defaultValue: "Ara" });

  const { handleNavigation } = useNavigation({
    selectedDistrict: hasDistrict ? district : emptySelection,
    selectedService: hasService ? service : emptySelection,
    onChangeSelectedData: onChange,
  });

  // A carried-over selection is always a district/service search, so keep the
  // shared tab on 0 in case the panel was last left on another tab.
  useEffect(() => {
    if (hasDistrict || hasService) setCurrentTab(0);
  }, [hasDistrict, hasService, setCurrentTab]);

  const openSearch = (focus: "district" | "service" | null) => {
    setCurrentTab(0);
    setInitialFocus(focus);
    setIsOpen(true);
  };

  // With a district/service already carried into the boxes, run the search
  // directly; otherwise open the panel so the user can pick one.
  const onSearch = () => {
    if (hasDistrict || hasService) {
      handleNavigation();
    } else {
      openSearch(null);
    }
  };

  return (
    <div className="w-full">
      {/* Desktop: existing search button */}
      {!isMobile && (
        <div>
          <SearchButton
            onClick={() => openSearch(null)}
            placeholder={inputPlaceholder}
            id="header-input"
          />
        </div>
      )}

      {/* Mobile: split district / service buttons + Ara */}
      {isMobile && (
        <div className="flex flex-col gap-3 w-full">
          <div className="flex gap-2 w-full">
            <SelectTrigger
              label={hasDistrict ? district.title : districtLabel}
              selected={hasDistrict}
              onClick={() => openSearch("district")}
            />
            <SelectTrigger
              label={hasService ? service.title : serviceLabel}
              selected={hasService}
              onClick={() => openSearch("service")}
            />
          </div>

          <div className="flex justify-center w-full">
            <Button
              onClick={onSearch}
              variant="primary-dark"
              className="!h-12 w-[160px] rounded-lg font-bold text-base"
            >
              {searchLabel}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SelectTrigger({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex-1 flex items-center justify-between gap-1 px-3 py-2 h-10",
        "rounded-lg border border-gray-300 bg-white text-left",
      )}
    >
      <span
        className={cn(
          "text-sm text-primary truncate",
          selected ? "font-semibold" : "font-medium",
        )}
      >
        {label}
      </span>
      <ChevronDownIcon className="size-4 text-gray-500 shrink-0" />
    </button>
  );
}
